import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui';
import { userFacingAddressLabel } from '@entities/security';
import type { WithdrawalAddressItem } from '@shared/api/contracts/security';
import type { WithdrawalNetwork } from '@shared/config/networks';
import { formatDateTime } from '@shared/lib/format-date-time';
import { normalizeWithdrawalNetworkCode } from '../../lib/network-balance';
import { getManualNetworkChainName } from '../../lib/usdt-network-meta';
import type { WithdrawalAddressFeedback } from '../../lib/manual-form';

interface WithdrawalAddressFieldProps {
  network: string;
  address: string;
  whitelistEnabled: boolean;
  items: WithdrawalAddressItem[];
  language: string;
  feedback: WithdrawalAddressFeedback | null;
  fieldError?: string;
  addressesLoading?: boolean;
  addressesError?: boolean;
  onChange: (address: string) => void;
  onBlur: () => void;
  onSwitchNetwork: (network: WithdrawalNetwork) => void;
}

export function WithdrawalAddressField({
  network,
  address,
  whitelistEnabled,
  items,
  language,
  feedback,
  fieldError,
  addressesLoading = false,
  addressesError = false,
  onChange,
  onBlur,
  onSwitchNetwork,
}: WithdrawalAddressFieldProps) {
  const { t } = useTranslation('cabinet');
  const message = fieldError || feedback?.message || '';
  const chainName = network ? getManualNetworkChainName(network) : '';
  const visibleItems = items.filter(
    (item) => item.address && normalizeWithdrawalNetworkCode(item.network) === normalizeWithdrawalNetworkCode(network),
  );
  const selected = visibleItems.find((item) => item.address === address);

  return (
    <div className="space-y-2">
      <Label htmlFor="withdrawal-address">{t('withdrawal.modal.address')}</Label>
      {whitelistEnabled ? (
        <Select value={address || undefined} onValueChange={onChange} disabled={!network}>
          <SelectTrigger id="withdrawal-address">
            <SelectValue placeholder={t('withdrawal.modal.addressPlaceholderSelect')} />
          </SelectTrigger>
          <SelectContent>
            {visibleItems.length === 0 ? (
              <div className="px-2 py-3 text-sm text-muted-foreground">
                {addressesLoading
                  ? t('common.loading', { ns: 'common' })
                  : addressesError
                    ? t('withdrawal.modal.addressLoadError')
                    : t('withdrawal.modal.addressEmpty')}
              </div>
            ) : visibleItems.map((item) => {
              const label = userFacingAddressLabel(item.label);
              const status = item.amlBlocked
                ? t('security.addressAmlBlocked')
                : item.status === 'pending'
                  ? t('security.addressPending', { datetime: formatDateTime(item.activeFrom ?? '', language) })
                  : '';
              return (
                <SelectItem key={item.id} value={item.address} disabled={item.amlBlocked === true} className="font-mono">
                  {[label, item.address, status].filter(Boolean).join(' · ')}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id="withdrawal-address"
          value={address}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={
            network
              ? t('withdrawal.modal.addressPlaceholder', { network, chain: chainName })
              : t('withdrawal.modal.addressPlaceholderEmpty')
          }
          className="font-mono text-[13px]"
          invalid={Boolean(message)}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
      )}
      {message ? (
        <p className="text-xs text-destructive">
          {message}
          {feedback?.suggestedNetwork ? (
            <>
              {' '}
              <button
                type="button"
                className="font-medium text-primary underline-offset-2 transition-colors duration-fast ease-standard hover:underline"
                onClick={() => onSwitchNetwork(feedback.suggestedNetwork as WithdrawalNetwork)}
              >
                {t('withdrawal.modal.switchNetwork', { network: feedback.suggestedNetwork })}
              </button>
            </>
          ) : null}
        </p>
      ) : selected?.amlBlocked ? (
        <p className="text-xs text-destructive">{t('security.addressAmlBlockedHint')}</p>
      ) : selected?.status === 'pending' ? (
        <p className="text-xs text-muted-foreground">
          {t('withdrawal.modal.cooling', { datetime: formatDateTime(selected.activeFrom ?? '', language) })}
        </p>
      ) : whitelistEnabled && network && visibleItems.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('withdrawal.modal.addressEmpty')}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {network
            ? t(`withdrawal.modal.addressHint.${network}`)
            : t('withdrawal.modal.addressHintEmpty')}
        </p>
      )}
      <Link to="/security" className="inline-flex min-h-tap items-center text-xs text-primary sm:min-h-0">
        {t('security.manageAddresses')}
      </Link>
    </div>
  );
}
